// Scripture Journal aggregation leaf (canon-spatial redesign, 2026-06-26).
//
// Pure, probe-importable: RELATIVE imports only (`./bsb-types`,
// `./highlight-utils`, type-only `../types/*`), no React / IDB / zustand / `@/`
// alias, so `tsx scripts/probe-journal-aggregate.mjs` loads it with default
// resolution (same posture as `scripture-anchor.ts` and `remember-moments.ts`).
//
// This module computes the Journal's STRUCTURE (which verses / chapters / books
// are marked, and the records attached to each) from the three existing sources
// (bookmarks, highlights, scripture-anchored prayers). It supersedes the inline
// `JournalRow` union that lived in BibleBookmarksPage. BSB verse TEXT is
// resolved in the page (async/IDB via `chapterVerseTexts`), NOT here.
//
// Gating stays at the call site: callers pass `[]` for a disabled source
// (highlights when v2 is off, prayers when Pray Mode is off), so counts and
// pages collapse byte-identically. Tombstone filtering also stays at the call
// site; the bookmark / highlight stores already drop deleted rows.

import { CANONICAL_BOOKS } from './bsb-types';
import { coerceFamily, HL_FAMILY_KEYS } from './highlight-utils';
import type { BibleBookmark } from '../types/bookmark';
import type { BibleHighlight, HighlightFamily } from '../types/highlight';
import type { Prayer } from '../types/prayer';

// Shared paging constants for the ACCUMULATING Journal surfaces (home search,
// meaning detail, prayed lens, and the passage "On this chapter" bucket). Pages
// render `rows.slice(0, visibleCount)` and bump by JOURNAL_PAGE_SIZE on "Show
// more" so the DOM never holds the full set (codex round-3 LOW). The passage
// page also previews at most PASSAGE_VERSE_NOTE_PREVIEW notes per verse inline,
// linking the overflow to the verse's reflection thread (codex round-4 LOW).
export const JOURNAL_PAGE_SIZE = 50;
export const PASSAGE_VERSE_NOTE_PREVIEW = 2;

export type JournalEntry =
  | { kind: 'note'; updatedAt: string; bookmark: BibleBookmark }
  | { kind: 'highlight'; updatedAt: string; highlight: BibleHighlight }
  | { kind: 'prayer'; updatedAt: string; prayer: Prayer };

export interface JournalSources {
  /** Live (tombstones already filtered by the store). */
  bookmarks: BibleBookmark[];
  /** Live; pass [] when BIBLE_READER_V2_ENABLED is off. */
  highlights: BibleHighlight[];
  /** Pass [] when PRAY_MODE_ENABLED is off; only scriptureAnchor != null is used. */
  prayers: Prayer[];
}

function ms(iso: string): number {
  return new Date(iso).getTime();
}

function firstVerse(verses: readonly number[]): number {
  let first = Number.POSITIVE_INFINITY;
  for (const verse of verses) {
    if (verse < first) first = verse;
  }
  return first;
}

/**
 * Flatten the three sources into one entry list. Prayers without a
 * `scriptureAnchor` are dropped (not journal entries). No sort; callers that
 * need recency sort on `updatedAt`.
 */
export function allEntries(s: JournalSources): JournalEntry[] {
  const notes: JournalEntry[] = s.bookmarks.map((bookmark) => ({
    kind: 'note',
    updatedAt: bookmark.updatedAt,
    bookmark,
  }));
  const highlights: JournalEntry[] = s.highlights.map((highlight) => ({
    kind: 'highlight',
    updatedAt: highlight.updatedAt,
    highlight,
  }));
  const prayers: JournalEntry[] = s.prayers
    .filter((p) => p.scriptureAnchor != null)
    .map((prayer) => ({ kind: 'prayer', updatedAt: prayer.updatedAt, prayer }));
  return [...notes, ...highlights, ...prayers];
}

// ── Entry accessors ──────────────────────────────────────────────────────────

export function entryBookSlug(e: JournalEntry): string {
  if (e.kind === 'note') return e.bookmark.anchor.bookSlug;
  if (e.kind === 'highlight') return e.highlight.bookSlug;
  return e.prayer.scriptureAnchor!.bookSlug;
}

export function entryChapter(e: JournalEntry): number {
  if (e.kind === 'note') return e.bookmark.anchor.chapter;
  if (e.kind === 'highlight') return e.highlight.chapter;
  return e.prayer.scriptureAnchor!.chapter;
}

export function entryReference(e: JournalEntry): string {
  if (e.kind === 'note') return e.bookmark.anchor.reference;
  if (e.kind === 'highlight') return e.highlight.reference;
  return e.prayer.scriptureAnchor!.reference;
}

// ── Front door ───────────────────────────────────────────────────────────────

export interface JournalCounts {
  notes: number;
  highlights: number;
  prayers: number;
  books: number;
}

export function totalCounts(s: JournalSources): JournalCounts {
  const entries = allEntries(s);
  const books = new Set(entries.map(entryBookSlug)).size;
  return {
    notes: s.bookmarks.length,
    highlights: s.highlights.length,
    prayers: entries.filter((e) => e.kind === 'prayer').length,
    books,
  };
}

export interface BookSummary {
  slug: string;
  name: string;
  bookTitle?: string;
  order: number;
  markCount: number;
  lastUpdatedAt: string;
}

/** Books with >=1 mark, in CANONICAL order. */
export function bookSummaries(s: JournalSources): BookSummary[] {
  const byBook = new Map<string, { count: number; last: string }>();
  for (const e of allEntries(s)) {
    const slug = entryBookSlug(e);
    const cur = byBook.get(slug);
    if (cur) {
      cur.count += 1;
      if (ms(e.updatedAt) > ms(cur.last)) cur.last = e.updatedAt;
    } else {
      byBook.set(slug, { count: 1, last: e.updatedAt });
    }
  }
  const result: BookSummary[] = [];
  for (const book of CANONICAL_BOOKS) {
    const agg = byBook.get(book.slug);
    if (!agg) continue;
    result.push({
      slug: book.slug,
      name: book.name,
      bookTitle: book.bookTitle,
      order: book.order,
      markCount: agg.count,
      lastUpdatedAt: agg.last,
    });
  }
  return result;
}

/** Top N books by markCount desc, lastUpdatedAt desc tiebreak. */
export function topBooks(s: JournalSources, n: number): BookSummary[] {
  return bookSummaries(s)
    .slice()
    .sort((a, b) => {
      if (b.markCount !== a.markCount) return b.markCount - a.markCount;
      return ms(b.lastUpdatedAt) - ms(a.lastUpdatedAt);
    })
    .slice(0, n);
}

/** The single entry with the max updatedAt across all sources, or null. */
export function mostRecentEntry(s: JournalSources): JournalEntry | null {
  let best: JournalEntry | null = null;
  for (const e of allEntries(s)) {
    if (best === null || ms(e.updatedAt) > ms(best.updatedAt)) best = e;
  }
  return best;
}

// ── "Your walk" canon heat grid ──────────────────────────────────────────────

export interface CanonHeatBook {
  slug: string;
  name: string;
  bookTitle?: string;
  /** Canonical order (1..66); OT is 1..39, NT is 40..66 (rule: derive testament). */
  order: number;
  /** Total marks (notes + highlights + anchored prayers) in this book; 0 if none. */
  markCount: number;
}

/**
 * Every book in canonical order with its total mark count (0 for unmarked), so
 * the home can render a 66-tile heat grid of the user's whole walk across
 * Scripture. The page splits OT/NT by `order` and buckets `markCount` into warmth
 * steps; the leaf only computes the structure (counts), never presentation.
 */
export function canonHeatmap(s: JournalSources): CanonHeatBook[] {
  const byBook = new Map<string, number>();
  for (const e of allEntries(s)) {
    const slug = entryBookSlug(e);
    byBook.set(slug, (byBook.get(slug) ?? 0) + 1);
  }
  return CANONICAL_BOOKS.map((b) => ({
    slug: b.slug,
    name: b.name,
    bookTitle: b.bookTitle,
    order: b.order,
    markCount: byBook.get(b.slug) ?? 0,
  }));
}

// ── Book page ────────────────────────────────────────────────────────────────

export interface ChapterSummary {
  chapter: number;
  markCount: number;
  /** Distinct, coerceFamily-cleaned, in HL_FAMILY_KEYS order. */
  families: HighlightFamily[];
  noteCount: number;
  prayerCount: number;
}

/** Chapters of one book that carry >=1 mark, ascending by chapter. */
export function chapterSummariesForBook(
  s: JournalSources,
  bookSlug: string,
): ChapterSummary[] {
  const byChapter = new Map<
    number,
    { count: number; families: Set<HighlightFamily>; notes: number; prayers: number }
  >();
  for (const e of allEntries(s)) {
    if (entryBookSlug(e) !== bookSlug) continue;
    const ch = entryChapter(e);
    let agg = byChapter.get(ch);
    if (!agg) {
      agg = { count: 0, families: new Set<HighlightFamily>(), notes: 0, prayers: 0 };
      byChapter.set(ch, agg);
    }
    agg.count += 1;
    if (e.kind === 'note') agg.notes += 1;
    else if (e.kind === 'prayer') agg.prayers += 1;
    else {
      const fam = coerceFamily(e.highlight.family);
      if (fam) agg.families.add(fam);
    }
  }
  return Array.from(byChapter.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([chapter, agg]) => ({
      chapter,
      markCount: agg.count,
      families: HL_FAMILY_KEYS.filter((f) => agg.families.has(f)),
      noteCount: agg.notes,
      prayerCount: agg.prayers,
    }));
}

// ── Passage page (structure only; verse TEXT resolved in the page) ───────────

export interface VerseMark {
  verse: number;
  highlight: BibleHighlight | null;
  /**
   * SINGLE-verse notes only (anchor covers exactly this verse), newest-first
   * by createdAt (COMPLETE; page bounds the render). Notes covering 2+ verses
   * live in ChapterMarks.passageNotes so one stored row renders ONCE, not
   * once per covered verse (the Chunk-1 dedup of the UX arc).
   */
  notes: BibleBookmark[];
  /** Anchored to this verse, newest-first (COMPLETE; page bounds the render). */
  prayers: Prayer[];
}

export interface ChapterMarks {
  /** Legacy whole-chapter (verses: []) bookmark notes, newest-first by createdAt. */
  chapterNotes: BibleBookmark[];
  /** Whole-chapter prayers (wholeChapter or verseless), newest-first. */
  chapterPrayers: Prayer[];
  /** Ascending by verse; only verses with >=1 mark. */
  verses: VerseMark[];
  /**
   * Notes whose anchor covers 2+ verses — ONE stored row each, rendered once
   * at the note's FIRST covered verse with its anchor.reference range label.
   * Sorted by first covered verse ascending, newest-first within a verse.
   * A verse covered ONLY by a passage note gets no VerseMark row (the passage
   * card carries its own verse texts); it still appears in `verses` when it
   * also carries a highlight, prayer, or single-verse note.
   */
  passageNotes: BibleBookmark[];
}

export function chapterMarks(
  s: JournalSources,
  bookSlug: string,
  chapter: number,
): ChapterMarks {
  const bookmarks = s.bookmarks.filter(
    (b) => b.anchor.bookSlug === bookSlug && b.anchor.chapter === chapter,
  );
  const highlights = s.highlights.filter(
    (h) => h.bookSlug === bookSlug && h.chapter === chapter,
  );
  const prayers = s.prayers.filter(
    (p) =>
      p.scriptureAnchor != null &&
      p.scriptureAnchor.bookSlug === bookSlug &&
      p.scriptureAnchor.chapter === chapter,
  );

  const isWholeChapterPrayer = (p: Prayer): boolean => {
    const a = p.scriptureAnchor!;
    return a.wholeChapter === true || a.verses.length === 0;
  };

  const chapterNotes = bookmarks
    .filter((b) => b.anchor.verses.length === 0)
    .sort((a, b) => ms(b.createdAt) - ms(a.createdAt));
  const chapterPrayers = prayers
    .filter(isWholeChapterPrayer)
    .sort((a, b) => ms(b.updatedAt) - ms(a.updatedAt));

  // One row per verse by construction (deterministic highlight id).
  const hlByVerse = new Map<number, BibleHighlight>();
  for (const h of highlights) hlByVerse.set(h.verse, h);

  // Chunk-1 dedup: a note covering 2+ verses is ONE stored row and renders
  // ONCE as a passage note instead of being attached to every covered verse.
  const notesByVerse = new Map<number, BibleBookmark[]>();
  for (const bookmark of bookmarks) {
    if (bookmark.anchor.verses.length !== 1) continue;
    const verse = bookmark.anchor.verses[0];
    const bucket = notesByVerse.get(verse);
    if (bucket) bucket.push(bookmark);
    else notesByVerse.set(verse, [bookmark]);
  }
  const passageNotes = bookmarks
    .filter((b) => b.anchor.verses.length > 1)
    .sort((a, b) => {
      const firstA = firstVerse(a.anchor.verses);
      const firstB = firstVerse(b.anchor.verses);
      if (firstA !== firstB) return firstA - firstB;
      return ms(b.createdAt) - ms(a.createdAt);
    });

  const verseSet = new Set<number>();
  for (const verse of notesByVerse.keys()) verseSet.add(verse);
  for (const h of highlights) verseSet.add(h.verse);
  const prayersByVerse = new Map<number, Prayer[]>();
  for (const p of prayers) {
    if (isWholeChapterPrayer(p)) continue;
    for (const verse of p.scriptureAnchor!.verses) {
      verseSet.add(verse);
      const bucket = prayersByVerse.get(verse);
      if (bucket) bucket.push(p);
      else prayersByVerse.set(verse, [p]);
    }
  }

  for (const bucket of notesByVerse.values()) {
    bucket.sort((a, b) => ms(b.createdAt) - ms(a.createdAt));
  }
  for (const bucket of prayersByVerse.values()) {
    bucket.sort((a, b) => ms(b.updatedAt) - ms(a.updatedAt));
  }

  const verses: VerseMark[] = Array.from(verseSet)
    .sort((a, b) => a - b)
    .map((verse) => ({
      verse,
      highlight: hlByVerse.get(verse) ?? null,
      notes: notesByVerse.get(verse) ?? [],
      prayers: prayersByVerse.get(verse) ?? [],
    }));

  return { chapterNotes, chapterPrayers, verses, passageNotes };
}

// ── Lenses ───────────────────────────────────────────────────────────────────

export interface FamilyCount {
  family: HighlightFamily;
  count: number;
}

/** Families with >=1 highlight, in HL_FAMILY_KEYS order (coerceFamily-cleaned). */
export function familyCounts(highlights: BibleHighlight[]): FamilyCount[] {
  const counts = new Map<HighlightFamily, number>();
  for (const h of highlights) {
    const fam = coerceFamily(h.family);
    if (!fam) continue;
    counts.set(fam, (counts.get(fam) ?? 0) + 1);
  }
  return HL_FAMILY_KEYS.filter((f) => counts.has(f)).map((family) => ({
    family,
    count: counts.get(family)!,
  }));
}

/**
 * Highlight families present in one book, count-DESC (HL_FAMILY_KEYS order as the
 * stable tiebreak), so `[0]?.family` is the book's dominant meaning. Drives the
 * per-book meaning color on the home browse list and the book page. coerceFamily
 * routes orphan slugs to plain (rule #55).
 */
export function bookHighlightFamilies(
  s: JournalSources,
  bookSlug: string,
): FamilyCount[] {
  const counts = new Map<HighlightFamily, number>();
  for (const h of s.highlights) {
    if (h.bookSlug !== bookSlug) continue;
    const fam = coerceFamily(h.family);
    if (!fam) continue;
    counts.set(fam, (counts.get(fam) ?? 0) + 1);
  }
  // Seed in HL_FAMILY_KEYS order, then stable-sort by count desc, so ties keep
  // canonical family order.
  return HL_FAMILY_KEYS.filter((f) => counts.has(f))
    .map((family) => ({ family, count: counts.get(family)! }))
    .sort((a, b) => b.count - a.count);
}

/** Highlights of one family, newest-first (coerceFamily routes orphan slugs to plain). */
export function highlightsForFamily(
  highlights: BibleHighlight[],
  family: HighlightFamily,
): BibleHighlight[] {
  return highlights
    .filter((h) => coerceFamily(h.family) === family)
    .sort((a, b) => ms(b.updatedAt) - ms(a.updatedAt));
}

/** Scripture-anchored prayers, newest-first. */
export function anchoredPrayers(prayers: Prayer[]): Prayer[] {
  return prayers
    .filter((p) => p.scriptureAnchor != null)
    .sort((a, b) => ms(b.updatedAt) - ms(a.updatedAt));
}
